import type { ContentImportItemDto } from "./types";
import { LEARNING_PATH_KIND } from "./learningPath/types";

const SUPPORTED_VERSION = 1;

export class ContentImportParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ContentImportParseError";
  }
}

function assertImportVersion(version: unknown): void {
  if (version === undefined) {
    return;
  }
  if (version !== SUPPORTED_VERSION) {
    throw new ContentImportParseError(
      `Unsupported version: ${String(version)}. Expected ${SUPPORTED_VERSION}.`
    );
  }
}

function asItemArray(value: unknown): ContentImportItemDto[] {
  if (!Array.isArray(value)) {
    throw new ContentImportParseError("Expected an array of items.");
  }
  if (value.length === 0) {
    throw new ContentImportParseError("No items to import.");
  }
  return value.map((item, index) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) {
      throw new ContentImportParseError(`Item at index ${index} must be an object.`);
    }
    return item as ContentImportItemDto;
  });
}

export function parseContentImportJson(parsed: unknown): ContentImportItemDto[] {
  if (Array.isArray(parsed)) {
    return asItemArray(parsed);
  }

  if (!parsed || typeof parsed !== "object") {
    throw new ContentImportParseError("JSON must be an object or array.");
  }

  const record = parsed as Record<string, unknown>;
  assertImportVersion(record.version);

  if (record.kind === LEARNING_PATH_KIND) {
    throw new ContentImportParseError(
      "This file is a full learning path bundle. Import it from the Learning Paths list, not from this collection."
    );
  }

  if (Array.isArray(record.items)) {
    return asItemArray(record.items);
  }

  if ("items" in record) {
    throw new ContentImportParseError('"items" must be a non-empty array.');
  }

  return [record as ContentImportItemDto];
}

export function parseContentImportFile(text: string): ContentImportItemDto[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new ContentImportParseError("Invalid JSON.");
  }
  return parseContentImportJson(parsed);
}
